import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Container,
    Heading,
    Text,
    Stack,
    Button,
    Spinner,
    Alert,
    AlertIcon,
    VStack,
    HStack,
    Badge,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    List,
    ListItem,
    Input,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon, TableContainer, Table, Thead, Tr, Th, Tbody, Td, FormLabel, FormControl, Grid, Checkbox
} from '@chakra-ui/react';
import { useAuth } from '../../contexts/AuthContext';
import { SupabaseClient } from '../../supabaseClient';
import { subDays } from 'date-fns';
import { DRI } from '../../components/analysis/dri';
import { useTranslation } from 'react-i18next';
import i18n from "i18next";
import { motion } from 'framer-motion';

export default function MealPlannerPage() {
    const MotionBox = motion(Box);
    const { user } = useAuth();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [issues, setIssues] = useState([]);
    const [, setError] = useState(null);
    const [planError, setPlanError] = useState(null);

    // Controls
    const [daysToAnalyze, setDaysToAnalyze] = useState(7);
    const [mealsPerDay, setMealsPerDay] = useState(3);
    const [preferences, setPreferences] = useState('');
    const [maxDistinct, setMaxDistinct] = useState(3);

    // Plan generation state
    const [plan, setPlan] = useState(null);
    const [triedLoad, setTriedLoad] = useState(false);
    const [planLoading, setPlanLoading] = useState(false);
    const [selectedMeals, setSelectedMeals] = useState([]);

    // Shopping-list state
    const [shoppingList, setShoppingList] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState(null);
    const [budget, setBudget] = useState('');

    useEffect(() => {
        if (!user) return;
        fetchLogsWithNutrients();
    }, [user, daysToAnalyze]);

    useEffect(() => {
        if (!user) return;
        async function fetchMealPlan() {
            const { data, error } = await SupabaseClient
                .from('meal_plans')
                .select('plan_data')
                .eq('profile_id', user.id)
                .order('created_at', { ascending: false })
                .limit(1)
                .single();

            if (!error && data) setPlan(data.plan_data);
            setTriedLoad(true);
        }
        fetchMealPlan();
    }, [user, daysToAnalyze]);

    useEffect(() => {
        if (!user || !plan) return

        async function fetchShoppingList() {
            const { data: existing, error: fetchError } = await SupabaseClient
                .from('shopping_lists')
                .select('list')
                .eq('profile_id', user.id)
                .order('created_at', { ascending: false })
                .limit(1)
                .single()

            if (!fetchError && existing?.list) {
                setShoppingList(existing.list)
            }
        }

        fetchShoppingList()
    }, [user, plan])

    function toggleMeal(dayIdx, mealIdx) {
        const key = `${dayIdx}-${mealIdx}`
        setSelectedMeals(arr =>
            arr.includes(key) ? arr.filter(k => k !== key) : [...arr, key]
        )
    }

    async function fetchLogsWithNutrients() {
        setLoading(true);
        setError(null);
        setPlan(null);

        const sinceDate = subDays(new Date(), daysToAnalyze - 1).toISOString();
        const { data, error: fetchErr } = await SupabaseClient
            .from('meal_logs')
            .select(
                `
                meal_date,
                food_description,
                meal_nutrients(
                    calories,
                    protein_g,
                    carbs_g,
                    fats_g,
                    fiber_g,
                    sugars_g,
                    vitamin_a_mcg,
                    vitamin_b1_mg,
                    vitamin_b2_mg,
                    vitamin_b3_mg,
                    vitamin_b5_mg,
                    vitamin_b6_mg,
                    vitamin_b7_mcg,
                    vitamin_b9_mcg,
                    vitamin_b12_mcg,
                    vitamin_c_mg,
                    vitamin_d_mcg,
                    vitamin_e_mg,
                    vitamin_k_mcg,
                    calcium_mg,
                    iron_mg,
                    magnesium_mg,
                    phosphorus_mg,
                    potassium_mg,
                    sodium_mg,
                    zinc_mg,
                    iodine_mcg,
                    selenium_mcg,
                    copper_mg,
                    manganese_mg,
                    chromium_mcg,
                    omega_3_mg,
                    omega_6_mg,
                    cholesterol_mg,
                    creatine_mg
                )
                `
            )
            .eq('profile_id', user.id)
            .gte('meal_date', sinceDate);

        if (fetchErr) {
            setError(fetchErr.message);
            setLoading(false);
            return;
        }

        const validRows = data.filter(row => row.meal_nutrients?.length);
        const daysCount = new Set(validRows.map(r => r.meal_date)).size;
        const nutrientEntries = validRows.flatMap(r => r.meal_nutrients);

        computeIssues(nutrientEntries, daysCount);
        setLoading(false);
    }

    async function generateShoppingList() {
        setListLoading(true);
        setListError(null);
        try {
            // pick only checked meals, if none checked, send them all
            const allMeals = planData.flatMap(d => d.meals)
            const mealsToSend = selectedMeals.size > 0 ? planData.flatMap((day, di) => day.meals.filter((m, mi) => selectedMeals.includes(`${di}-${mi}`))): allMeals
            const res = await fetch('/api/generateShoppingList', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: user.id,
                    meals: mealsToSend,
                    budget: parseFloat(budget) || 0,
                    language: i18n.language,
                })
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'API Error');
            console.log(json)
            setShoppingList(json.list || []);
        } catch (err) {
            setListError(err.message);
        } finally {
            setListLoading(false);
        }
    }

    function computeIssues(logs, days) {
        if (!logs.length || days === 0) { setIssues([]); return; }
        const totals = logs.reduce((acc, entry) => {
            Object.keys(DRI).forEach(key => {
                if (DRI[key]) acc[key] = (acc[key] || 0) + (entry[key] || 0);
            });
            return acc;
        }, {});
        const averages = {};
        Object.entries(totals).forEach(([k,v]) => averages[k] = v / days);
        const list = Object.entries(DRI)
            .filter(([_,r]) => r)
            .map(([nutrient, r]) => {
                const avg = averages[nutrient]||0;
                const below = r.min!=null&&avg<r.min? r.min-avg:0;
                const above = r.max!=null&&avg>r.max? avg-r.max:0;
                return {nutrient,average:avg,below,above};
            })
            .filter(i=>i.below>0||i.above>0)
            .sort((a,b)=>b.below-a.below||b.above-a.above);
        setIssues(list);
    }

    async function generateMealPlan() {
        setPlanLoading(true);
        setPlanError(null);
        setPlan(null);
        setShoppingList([])
        try {
            const res = await fetch('/api/generateMealPlan', {
                method: 'POST',
                headers: {'Content-Type':'application/json'},
                body: JSON.stringify({
                    userId: user.id,
                    daysToAnalyze,
                    mealsPerDay,
                    preferences: preferences.split(',').map(s=>s.trim()).filter(Boolean),
                    language: i18n.language,
                    budget: budget,
                    maxDistinct
                })
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error||'API Error');
            setPlan(typeof json.plan==='string'? JSON.parse(json.plan): json.plan);
        } catch(err) {
            setPlanError(err.message);
        } finally {
            setPlanLoading(false);
        }
    }

    const planData = useMemo(() => plan || null, [plan]);

    return (
        <Container maxW="container.lg" py={8}>
            <Heading mb={4}>{t('mealPlanner.title')}</Heading>

            <VStack mb={6} spacing={4}>
                {/* Row 1: inputs */}
                <Grid
                    templateColumns={{ base: '1fr', sm: 'repeat(3, 5fr)', md: 'repeat(5, 1fr)' }}
                    gap={4}
                    w="full"
                >
                    <FormControl>
                        <FormLabel>{t('controls.daysToAnalyze')}</FormLabel>
                        <NumberInput
                            size="sm"
                            min={1}
                            max={7}
                            value={daysToAnalyze}
                            onChange={(_, v) => setDaysToAnalyze(v)}
                            w="100%"
                        >
                            <NumberInputField />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                    </FormControl>

                    <FormControl>
                        <FormLabel>{t('controls.mealsPerDay')}</FormLabel>
                        <NumberInput
                            size="sm"
                            min={1}
                            max={10}
                            value={mealsPerDay}
                            onChange={(_, v) => setMealsPerDay(v)}
                            w="100%"
                        >
                            <NumberInputField />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                    </FormControl>

                    <FormControl>
                        <FormLabel>Budget ($)</FormLabel>
                        <NumberInput
                            size="sm"
                            min={0}
                            precision={2}
                            value={budget}
                            onChange={(_, v) => setBudget(v)}
                            w="100%"
                        >
                            <NumberInputField placeholder="optional" />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                    </FormControl>

                    <FormControl>
                        <FormLabel>{t('controls.preferences')}</FormLabel>
                        <Input
                            size="sm"
                            placeholder={t('controls.preferencesPlaceholder')}
                            value={preferences}
                            onChange={e => setPreferences(e.target.value)}
                            w="100%"
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel>Max distinct meals</FormLabel>
                        <NumberInput
                            size="sm"
                            min={1}
                            max={daysToAnalyze * mealsPerDay}
                            value={maxDistinct}
                            onChange={(_, v) => setMaxDistinct(v)}
                            w="100%"
                        >
                            <NumberInputField placeholder="e.g. 3" />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                    </FormControl>
                </Grid>

                {/* Row 2: action buttons */}
                <HStack spacing={4} w="full">
                    <Button colorScheme="teal" onClick={generateMealPlan}>
                        {t('controls.generateMeals')}
                    </Button>
                    <Button
                        colorScheme="purple"
                        onClick={generateShoppingList}
                        isLoading={listLoading}
                    >
                        Generate Shopping List
                    </Button>
                </HStack>
            </VStack>

            <HStack align="start" spacing={8} w="full">
                <Box flex="1">
                    {loading&&<Spinner/>}{planLoading&&<Spinner/>}{planError&&<Alert status="error"><AlertIcon/>{planError}</Alert>}
                    {!planLoading&&triedLoad&&planData&&(
                        <VStack align="start" spacing={6}>
                            <Accordion w="full" allowToggle defaultIndex={[0]}>
                                {planData.map((day, dayIdx)=>(
                                    <MotionBox key={dayIdx} initial={{opacity:0,y:-10}} animate={{opacity:1,y:0}} transition={{duration:0.2}}>
                                        <AccordionItem borderRadius="md" mb={4} boxShadow="sm">
                                            <AccordionButton _expanded={{bg:'teal.50'}}>
                                                <Box flex="1" textAlign="left" fontWeight="bold">{t('plan.dayLabel',{day:day.day})}</Box>
                                                <AccordionIcon />
                                            </AccordionButton>
                                            <AccordionPanel pb={4}>
                                                <List spacing={3}>{day.meals.map((m,i)=> {
                                                    const key = `${dayIdx}-${i}`
                                                    return (
                                                        <ListItem key={i}>
                                                            <Checkbox
                                                                isChecked={selectedMeals.includes(key)}
                                                                onChange={() => toggleMeal(dayIdx, i)}
                                                                mr={3}/>
                                                            <Box>
                                                                <Text fontWeight="bold">
                                                                    {t('plan.mealLabel',{num:i+1})}
                                                                </Text>
                                                            </Box>
                                                            <Text>
                                                                {m.description}
                                                            </Text>
                                                        </ListItem>)})}
                                                </List>
                                            </AccordionPanel>
                                        </AccordionItem>
                                    </MotionBox>
                                ))}
                            </Accordion>
                        </VStack>
                    )}
                    {!planLoading&&triedLoad&&!planData&&(
                        <Stack spacing={6}>
                            {issues.length===0?
                                <Text>{t('noIssues',{days:daysToAnalyze})}</Text>
                                :
                                <VStack align="stretch" spacing={4}>
                                    <Text>{t('summary.heading')}</Text>
                                    {issues.slice(0,5).map(({nutrient,average,below,above})=>(
                                        <Box key={nutrient} p={4} borderWidth={1} borderRadius="md" w="100%">
                                            <HStack justify="space-between">
                                                <Text fontWeight="bold" textTransform="capitalize">{nutrient.replace(/_/g,' ')}</Text>
                                                {below>0&&<Badge colorScheme="orange">{Math.round(below)} {t('summary.below')}</Badge>}
                                                {above>0&&<Badge colorScheme="red">{Math.round(above)} {t('summary.above')}</Badge>}
                                            </HStack>
                                            <Text fontSize="sm">{t('summary.avg',{avg:Math.round(average)})}{DRI[nutrient].min!=null?` | ${t('summary.min',{val:DRI[nutrient].min})}`:''}{DRI[nutrient].max!=null?` | ${t('summary.max',{val:DRI[nutrient].max})}`:''}</Text>
                                        </Box>
                                    ))}
                                </VStack>
                            }
                        </Stack>
                    )}
                </Box>

                <Box flex="1" p={4} borderWidth="1px" borderRadius="md" bg="gray.50" overflowX="auto">
                    <Heading size="sm" mb={4}>Shopping List</Heading>
                    {listError&&<Text color="red.500" mb={2}>{listError}</Text>}
                    {listLoading&&<Spinner />}
                    {shoppingList.length > 0 ? (
                        <TableContainer maxH="400px" overflowY="auto" overflowX="auto">
                            <Table variant="striped" size="sm">
                                <Thead position="sticky" top={0} bg="gray.100" zIndex={1}>
                                    <Tr>
                                        <Th>Item</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {shoppingList.map(({item, quantity, estimatedCost}, idx) => (
                                        <Tr key={idx}>
                                            <Td>{item}</Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    ) :(!listLoading&&<Text fontSize="sm" color="gray.500">No list generated yet.</Text>)}
                </Box>
            </HStack>
        </Container>
    );
}